import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { profileAPI } from "../services/api";
import ProfileDetailBody from "./ProfileDetailBody";

const FALLBACK_IMAGE = "/default-avatar.png";

const getProfileId = (profileLike) => {
  if (!profileLike) return null;
  return (
    profileLike.userId ||
    profileLike._id ||
    profileLike.id ||
    profileLike?.user?.id ||
    null
  );
};

function UserProfileModal({
  userId,
  isOpen = false,
  onClose,
  onLike,
  onSkip,
  disableActions = false,
  prefetchedProfile = null,
  renderAsPage = false,
  swipeStatus = null, // { status: null | "liked" | "passed" | "match", matchId: string }
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [photoIndex, setPhotoIndex] = useState(0);
  const navigate = useNavigate();

  const isMountedRef = useRef(true);
  const shouldRender = renderAsPage ? true : Boolean(isOpen);
  const showActions = Boolean(onLike || onSkip);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (renderAsPage || !isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, renderAsPage]);

  useEffect(() => {
    if (!prefetchedProfile) return;
    const prefetchedId = getProfileId(prefetchedProfile);
    const targetId = userId ? userId.toString() : null;
    if (!prefetchedId || !targetId) return;
    if (prefetchedId.toString() !== targetId) return;

    setProfile((prev) => {
      const prevId = getProfileId(prev);
      if (prevId && prevId.toString() === targetId) {
        return prev;
      }
      return prefetchedProfile;
    });
  }, [prefetchedProfile, userId]);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return;
    }
    setProfile((prev) => {
      if (!prev) return prev;
      const prevId = getProfileId(prev);
      if (prevId && prevId.toString() === userId.toString()) {
        return prev;
      }
      return null;
    });
  }, [userId]);

  const fetchProfile = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const response = await profileAPI.getProfileByUserId(userId);
      if (!isMountedRef.current) return;
      setProfile(response.data.data);
    } catch (err) {
      console.error("[UserProfileModal] Error loading profile", err);
      if (!isMountedRef.current) return;
      setError(err.response?.data?.message || "Unable to load profile");
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!shouldRender || !userId) return;
    setPhotoIndex(0);
    fetchProfile();
  }, [shouldRender, fetchProfile, userId]);

  const snapshot = profile || prefetchedProfile || {};
  const photos = useMemo(() => {
    if (snapshot.photos?.length) {
      return snapshot.photos.filter(Boolean);
    }
    return [FALLBACK_IMAGE];
  }, [snapshot.photos]);

  useEffect(() => {
    if (photoIndex > photos.length - 1) {
      setPhotoIndex(0);
    }
  }, [photos.length, photoIndex]);

  const hasProfile = Boolean(Object.keys(snapshot).length);

  const handleOverlayClick = (event) => {
    if (renderAsPage) return;
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const handlePrev = () => {
    setPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  if (!shouldRender) {
    return null;
  }

  const overlayClass = renderAsPage
    ? "min-h-screen bg-slate-100 px-4 py-10 flex items-start justify-center"
    : "fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center px-3 sm:px-4 py-4 sm:py-6 z-[9999] overflow-y-auto";

  const content = (
    <div className={overlayClass} onClick={handleOverlayClick}>
      <div className="relative w-full max-w-[520px] sm:max-w-[560px] md:max-w-[640px] mx-auto my-auto">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[95vh] h-auto">
          <div
            className={`profile-scroll flex flex-col overflow-y-auto ${renderAsPage ? "profile-view-card" : ""}`}
          >
            <div className="relative flex-shrink-0 bg-gray-50">
              <img
                src={photos[photoIndex] || FALLBACK_IMAGE}
                alt={snapshot?.name || "User"}
                className="w-full h-auto object-contain"
              />
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrev();
                    }}
                    className="absolute top-1/2 left-2 sm:left-4 -translate-y-1/2 bg-white/85 hover:bg-white text-gray-700 w-8 h-8 sm:w-10 sm:h-10 rounded-full shadow flex items-center justify-center text-lg sm:text-xl"
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNext();
                    }}
                    className="absolute top-1/2 right-2 sm:right-4 -translate-y-1/2 bg-white/85 hover:bg-white text-gray-700 w-8 h-8 sm:w-10 sm:h-10 rounded-full shadow flex items-center justify-center text-lg sm:text-xl"
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                  <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-1.5">
                    {photos.map((_, idx) => (
                      <span
                        key={`dot-${idx}`}
                        className={`h-1 sm:h-1.5 rounded-full transition-all ${
                          idx === photoIndex
                            ? "w-6 sm:w-8 bg-white"
                            : "w-3 sm:w-4 bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                className="absolute top-3 sm:top-4 right-3 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/90 text-gray-700 font-semibold shadow hover:bg-white text-base sm:text-lg"
                aria-label="Close profile"
              >
                ✕
              </button>
            </div>

            <div className="px-3 sm:px-4 md:px-5 pt-3 sm:pt-4 pb-3 sm:pb-4 space-y-3 sm:space-y-4">
              {loading && !profile && !error && (
                <div className="py-4 sm:py-6 flex flex-col items-center gap-2 text-gray-500">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
                  <p className="text-xs sm:text-sm font-medium">
                    Loading profile…
                  </p>
                </div>
              )}

              {error && (
                <div className="p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl">
                  <p className="text-xs sm:text-sm text-red-600 font-semibold mb-2">
                    {error}
                  </p>
                  <button
                    type="button"
                    onClick={fetchProfile}
                    className="px-3 py-1.5 text-xs sm:text-sm font-semibold text-white bg-red-500 rounded-full hover:bg-red-600"
                  >
                    Retry
                  </button>
                </div>
              )}

              {hasProfile && !error && <ProfileDetailBody profile={snapshot} />}
            </div>
          </div>

          {/* Action buttons or status messages */}
          {!error && (
            <>
              {/* Show match status */}
              {swipeStatus?.status === "match" && swipeStatus?.matchId && (
                <div className="flex-shrink-0 border-t border-gray-100 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 flex flex-col gap-2 bg-gradient-to-r from-pink-50 to-indigo-50">
                  <p className="text-sm font-semibold text-center text-indigo-700">
                    🎉 You matched with {snapshot?.name || "this person"}!
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/chat/${swipeStatus.matchId}`);
                      onClose?.();
                    }}
                    className="w-full px-4 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 text-white text-sm font-semibold shadow hover:shadow-lg"
                  >
                    Start Chat 💬
                  </button>
                </div>
              )}

              {/* Show liked status */}
              {swipeStatus?.status === "liked" && (
                <div className="flex-shrink-0 border-t border-gray-100 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 bg-pink-50">
                  <p className="text-sm font-semibold text-center text-pink-700">
                    ❤️ You already liked this profile
                  </p>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    We'll notify you if they like you back!
                  </p>
                </div>
              )}

              {/* Show passed status */}
              {swipeStatus?.status === "passed" && (
                <div className="flex-shrink-0 border-t border-gray-100 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 bg-gray-50">
                  <p className="text-sm font-semibold text-center text-gray-700">
                    👋 You passed on this profile
                  </p>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    View only mode
                  </p>
                </div>
              )}

              {/* Show Like/Pass buttons only if no prior swipe */}
              {!swipeStatus?.status && showActions && (
                <div className="flex-shrink-0 border-t border-gray-100 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 flex flex-col sm:flex-row gap-2 bg-white">
                  {onSkip && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disableActions) onSkip();
                      }}
                      disabled={disableActions}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
                    >
                      Pass
                    </button>
                  )}
                  {onLike && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disableActions) onLike();
                      }}
                      disabled={disableActions}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 text-white text-xs sm:text-sm font-semibold shadow hover:shadow-lg disabled:opacity-60"
                    >
                      Like
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // When used as a full page, render inline. Otherwise, portal to document.body
  if (renderAsPage) {
    return content;
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(content, document.body);
}

export default UserProfileModal;
